import React, { useRef, useState } from 'react';
import './chat.css';

const FLAME = 'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z';
const SPARKS = [0, 1, 2, 3, 4, 5, 6, 7];
const EMBERS = [-12, -5, 3, 9, 14];
const CONFETTI = ['#ff6a13', '#fbbf24', '#16a34a', '#2563eb', '#dc2626', '#a855f7'];
const CONFETTI_PIECES = Array.from({ length: 14 }, (_, i) => i);
const DOUBLE_TAP_MS = 350;

// a bet on the leaderboard: click the flame, or double-click the bet, to like it.
// The animations only use transforms and opacity, so nothing around them moves or waits for them.
export default function BetLike({ className, style, liked, count, onToggle, label, children }) {
  const [pop, setPop] = useState(0);       // replays the ignite: flame, sparks, embers
  const [fizz, setFizz] = useState(0);     // replays the flame going out
  const [big, setBig] = useState(0);       // replays the big flame over the bet

  const lastTap = useRef(null);            // time + spot of the last tap
  const skipClick = useRef(false);         // the flame's click right after a double tap
  const shownCount = useRef(count);
  const countDir = useRef(null);           // which way the number rolls
  if (count !== shownCount.current) {
    countDir.current = count > shownCount.current ? 'up' : 'down';
    shownCount.current = count;
  }

  const clickFlame = (e) => {
    e.stopPropagation();
    if (skipClick.current) { skipClick.current = false; return; }
    if (liked) setFizz((n) => n + 1);
    else setPop((n) => n + 1);
    onToggle();
  };

  // counted by hand: iPhones don't send a double-click event, and this one works the same with a mouse or a finger
  const pointerUp = (e) => {
    const other = e.target.closest('button');
    if (other && !other.classList.contains('bet-like')) return;
    const last = lastTap.current;
    const isDouble = last && e.timeStamp - last.t < DOUBLE_TAP_MS && Math.hypot(e.clientX - last.x, e.clientY - last.y) < 24;
    if (!isDouble) { lastTap.current = { t: e.timeStamp, x: e.clientX, y: e.clientY }; return; }
    lastTap.current = null;
    skipClick.current = !!other;
    setBig((n) => n + 1);
    setPop((n) => n + 1);
    if (!liked) onToggle();                // a double tap only ever adds the like
  };

  return (
    <div
      className={`${className} bet-likeable`}
      style={style}
      onPointerUp={pointerUp}
      onMouseDown={(e) => { if (e.detail > 1) e.preventDefault(); }}   // no selected text on a double-click
    >
      {children}
      <button type="button" className={`bet-like${liked ? ' liked' : ''}${count >= 3 ? ' hot' : ''}`} aria-pressed={liked} aria-label="Like" onClick={clickFlame}>
        <span className="bet-like-icon" key={`${pop}-${fizz}`} data-pop={(pop > 0 && liked) || undefined} data-fizz={(fizz > 0 && !liked) || undefined}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={FLAME} /></svg>
          {pop > 0 && liked && (
            <>
              <u className="bet-like-ring" />
              {SPARKS.map((i) => <i key={i} className="bet-like-spark" style={{ '--a': `${i * 45}deg`, '--d': `${i % 2 ? 20 : 30}px` }} />)}
              {EMBERS.map((x, i) => <i key={x} className="bet-like-ember" style={{ '--x': `${x}px`, animationDelay: `${i * 50}ms` }} />)}
              {label && CONFETTI_PIECES.map((i) => <i key={i} className="bet-like-confetti" style={{ '--a': `${i * 26 - 170}deg`, '--d': `${34 + (i % 4) * 14}px`, background: CONFETTI[i % CONFETTI.length] }} />)}
            </>
          )}
          {fizz > 0 && !liked && <i className="bet-like-smoke" />}
        </span>
        {label && !liked && <span className="bet-like-label">{label}</span>}
        {count > 0 && <b key={count} className={countDir.current ? `roll-${countDir.current}` : undefined}>{count}</b>}
      </button>
      {big > 0 && (
        <span className="bet-like-big" key={big} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="84" height="84"><path d={FLAME} /></svg>
        </span>
      )}
    </div>
  );
}
